import React, { useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import MoodBadIcon from '@mui/icons-material/MoodBad';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import { formatDistanceToNow } from 'date-fns';
import {
  getPublicStoryUrl,
  softDeleteStory,
  Story,
  StoryReaction,
  upsertReaction,
} from '../../services/supabase/story.service';

const REACTIONS: Array<{ key: StoryReaction; label: string; icon: React.ReactNode }> = [
  { key: 'like', label: 'Like', icon: <ThumbUpIcon fontSize="small" /> },
  { key: 'love', label: 'Love', icon: <FavoriteIcon fontSize="small" /> },
  { key: 'laugh', label: 'Haha', icon: <EmojiEmotionsIcon fontSize="small" /> },
  { key: 'wow', label: 'Wow', icon: <AutoAwesomeIcon fontSize="small" /> },
  { key: 'sad', label: 'Sad', icon: <MoodBadIcon fontSize="small" /> },
  { key: 'angry', label: 'Angry', icon: <SentimentSatisfiedAltIcon fontSize="small" /> },
];

const REACTION_EMOJI: Record<StoryReaction, string> = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😡',
};

export default function StoryViewerDialog(props: {
  open: boolean;
  onClose: () => void;
  stories: Story[];
  activeIndex: number;
  onChangeIndex: (idx: number) => void;
  userId: string;
  myReactionByStoryId: Record<string, StoryReaction | null>;
  reactionCountsByStoryId: Record<string, Record<StoryReaction, number>>;
  onReacted: () => void | Promise<void>;
}) {
  const {
    open,
    onClose,
    stories,
    activeIndex,
    onChangeIndex,
    userId,
    myReactionByStoryId,
    reactionCountsByStoryId,
    onReacted,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const story = stories[activeIndex];
  const imgUrl = story ? getPublicStoryUrl(story.image_path) : '';
  const createdAgo = story ? formatDistanceToNow(new Date(story.created_at), { addSuffix: true }) : '';

  const myReaction = story ? myReactionByStoryId[story.id] || null : null;
  const counts = story ? reactionCountsByStoryId[story.id] : undefined;

  const totalReacts = useMemo(() => {
    if (!counts) return 0;
    return Object.values(counts).reduce((s, v) => s + (Number(v) || 0), 0);
  }, [counts]);

  const [floaters, setFloaters] = useState<Array<{ id: string; emoji: string; leftPct: number }>>([]);
  const [deleting, setDeleting] = useState(false);
  const floaterSeed = useRef(0);

  const spawnFloater = (reaction: StoryReaction) => {
    const id = `${Date.now()}_${floaterSeed.current++}`;
    const leftPct = 10 + Math.random() * 80;
    const emoji = REACTION_EMOJI[reaction];
    setFloaters((prev) => [...prev, { id, emoji, leftPct }]);
    window.setTimeout(() => {
      setFloaters((prev) => prev.filter((f) => f.id !== id));
    }, 1200);
  };

  const handleReact = async (reaction: StoryReaction) => {
    if (!story) return;
    spawnFloater(reaction);
    try {
      await upsertReaction({ storyId: story.id, userId, reaction });
      onReacted();
    } catch {
      // keep UI simple in v1 (no toast infra here); the caller refetch will reconcile
    }
  };

  const isOwnStory = story?.author_id === userId;

  const handleDeleteOwnStory = async () => {
    if (!story || !isOwnStory || deleting) return;
    if (!window.confirm('Delete this story? It will disappear for everyone.')) return;
    setDeleting(true);
    try {
      const oldLen = stories.length;
      await softDeleteStory({ storyId: story.id, authorId: userId });
      if (oldLen <= 1) {
        onClose();
      } else {
        const newIdx = activeIndex >= oldLen - 1 ? oldLen - 2 : activeIndex;
        onChangeIndex(newIdx);
      }
      await onReacted();
    } catch {
      // same as reactions — refetch on retry
    } finally {
      setDeleting(false);
    }
  };

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < stories.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ position: 'relative', bgcolor: 'black' }}>
        <Box
          component="img"
          src={imgUrl}
          alt="Story"
          sx={{
            width: '100%',
            height: isMobile ? '70vh' : 520,
            objectFit: 'contain',
            display: 'block',
            backgroundColor: 'black',
          }}
        />

        {/* Floating reactions (Facebook-style) */}
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          {floaters.map((f) => (
            <Box
              key={f.id}
              sx={{
                position: 'absolute',
                left: `${f.leftPct}%`,
                bottom: 18,
                fontSize: 26,
                filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.45))',
                animation: 'storyFloaterUp 1.2s ease-out forwards',
                '@keyframes storyFloaterUp': {
                  '0%': { transform: 'translate(-50%, 0) scale(0.9)', opacity: 0.0 },
                  '10%': { opacity: 1 },
                  '100%': { transform: 'translate(-50%, -220px) scale(1.15)', opacity: 0 },
                },
              }}
            >
              {f.emoji}
            </Box>
          ))}
        </Box>

        {/* Top bar */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, p: 1.25, color: 'white' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                src={story?.author_avatar_url || undefined}
                sx={{ width: 34, height: 34, border: '2px solid rgba(255,255,255,0.9)' }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }} noWrap>
                  {story?.author_username || 'Story'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {createdAgo}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isOwnStory && (
                <Tooltip title="Delete your story" arrow>
                  <span>
                    <IconButton
                      onClick={() => void handleDeleteOwnStory()}
                      disabled={deleting}
                      sx={{ color: 'white' }}
                      aria-label="delete story"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="close story">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        {/* Prev/Next */}
        {!isMobile && (
          <>
            <IconButton
              onClick={() => canPrev && onChangeIndex(activeIndex - 1)}
              disabled={!canPrev}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
              aria-label="previous story"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => canNext && onChangeIndex(activeIndex + 1)}
              disabled={!canNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
              }}
              aria-label="next story"
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Box>

      {/* Caption + reactions */}
      <Box sx={{ p: 1.5 }}>
        {story?.caption && (
          <Typography sx={{ mb: 1.25, fontWeight: 550 }}>{story.caption}</Typography>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {REACTIONS.map((r) => (
            <Tooltip key={r.key} title={r.label} arrow>
              <Chip
                icon={r.icon as any}
                label={r.label}
                clickable
                onClick={() => handleReact(r.key)}
                color={myReaction === r.key ? 'primary' : 'default'}
                variant={myReaction === r.key ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700 }}
              />
            </Tooltip>
          ))}

          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 650 }}>
            {totalReacts} reaction{totalReacts === 1 ? '' : 's'}
          </Typography>
        </Stack>
      </Box>
    </Dialog>
  );
}

